import { useState } from 'react';
import { registerUser } from '../../firebase/register';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import Box from "@mui/joy/Box";
import Link from "@mui/joy/Link";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";

import PhoneInput from '../../components/form/PhoneInput';
import { useSnackbar } from '../../utils/SnackbarContext';

import PasswordRequirements from '../../components/PasswordRequirements';

const validationSchema = Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    companyName: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    phoneNumber: Yup.string().required('Required'),
    password: Yup.string().required('Required')
        .min(8)
        .max(20)
        .matches(/(?=.*[A-Z])/)
        .matches(/(?=.*[a-z])/)
        .matches(/(?=.*[0-9])/),
    passwordConfirm: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Required'),
});

export default function RegisterForm() {
    const showSnackbar = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1); // Step control

    const navigate = useNavigate();

    const formik = useFormik({
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            firstName: '',
            lastName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            password: '',
            passwordConfirm: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const { email, password, firstName, lastName, companyName, phoneNumber } = values;
            const data = { email, password, firstName, lastName, companyName, phoneNumber };
            registerUser(data)
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        showSnackbar("User registered successfully. Please check your email for verification.", "success");
                        navigate('/login');
                    } else {
                        showSnackbar(data.message, "danger");
                    }
                })
                .catch(error => showSnackbar(error.message, "danger"))
                .finally(() => setLoading(false));
        }
    });

    const handleNextStep = () => {
        if (!formik.values.firstName){
            formik.setFieldError('firstName', 'Required');
        }
        if (!formik.values.lastName){
            formik.setFieldError('lastName', 'Required');
        }
        if (!formik.values.companyName){
            formik.setFieldError('companyName', 'Required');
        }
        if (formik.values.firstName && formik.values.lastName && formik.values.companyName){
            setCurrentStep(currentStep + 1);
        }
    };
    
    const handlePreviousStep = () => {
        setCurrentStep(currentStep - 1);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack direction="column" spacing={1}>
                <Typography level="h2">Welcome to TrueKYC</Typography>
                <RouterLink to="/login">
                    <Link level="body-sm">Already have an account? Log In</Link>
                </RouterLink>

                {currentStep === 1 && (
                    <>
                        <FormControl error={!!formik.errors.firstName} sx={{ marginRight: 0.5 }}>
                            <FormLabel>First Name</FormLabel>
                            <Input
                                type="text"
                                name="firstName"
                                onChange={formik.handleChange}
                                value={formik.values.firstName}
                            />
                            {formik.errors.firstName ? (
                                <FormHelperText>{formik.errors.firstName}</FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl error={!!formik.errors.lastName} sx={{ marginLeft: 0.5 }}>
                            <FormLabel>Last Name</FormLabel>
                            <Input
                                type="text"
                                name="lastName"
                                onChange={formik.handleChange}
                                value={formik.values.lastName}
                            />
                            {formik.errors.lastName ? (
                                <FormHelperText>{formik.errors.lastName}</FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl error={!!formik.errors.companyName}>
                            <FormLabel>Company Name</FormLabel>
                            <Input
                                type="text"
                                name="companyName"
                                onChange={formik.handleChange}
                                value={formik.values.companyName}
                            />
                            {formik.errors.companyName ? (
                                <FormHelperText>{formik.errors.companyName}</FormHelperText>
                            ) : null}
                        </FormControl>
                        <Button size="sm" onClick={handleNextStep}>Next</Button>
                    </>
                )}

                {currentStep === 2 && (
                    <>
                        <FormControl error={!!formik.errors.email}>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                name="email"
                                onChange={formik.handleChange}
                                value={formik.values.email}
                            />
                            {formik.errors.email ? (
                                <FormHelperText>{formik.errors.email}</FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl error={!!formik.errors.phoneNumber}>
                            <FormLabel>Phone Number</FormLabel>
                            <PhoneInput
                                name="phoneNumber"
                                setFieldValue={formik.setFieldValue}
                                formik={formik}
                            />
                            {formik.errors.phoneNumber ? (
                                <FormHelperText>{formik.errors.phoneNumber}</FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl error={!!formik.errors.password}>
                            <FormLabel>Password</FormLabel>
                            <Input
                                type="password"
                                name="password"
                                onChange={formik.handleChange}
                                value={formik.values.password}
                            />
                        </FormControl>
                        <FormControl error={!!formik.errors.passwordConfirm}>
                            <FormLabel>Confirm Password</FormLabel>
                            <Input
                                type="password"
                                name="passwordConfirm"
                                onChange={formik.handleChange}
                                value={formik.values.passwordConfirm}
                            />
                            {formik.errors.passwordConfirm ? (
                                <FormHelperText>{formik.errors.passwordConfirm}</FormHelperText>
                            ) : null}
                            <PasswordRequirements password={formik.values.password} />
                        </FormControl>
                        <Stack direction="column" spacing={1}>
                            <Button size="sm" type="submit" loading={loading} disabled={loading}>
                                Create Account
                            </Button>
                            <Button size="sm" variant="soft" onClick={handlePreviousStep}>Back</Button>
                            <Box textAlign="center">
                                <Typography level="body-xs">By clicking "Create Account", you agree to TrueKYC's Terms And Conditions, Privacy Policy and Data Processing Aggreement</Typography>
                            </Box>
                        </Stack>
                    </>
                )}

            </Stack>
        </form>
    );
}
